import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DatePickerDMY from '../components/DatePickerDMY';
import {
  ListChecks, Eye, PencilSimple, Trash, MagnifyingGlass, FunnelSimple, X, CalendarBlank, ArrowLeft, Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 996 — Dedicated Tasks Management page. Complements (does NOT
// replace) the Calendar page: it fetches from the SAME
// `/api/calendar/tasks` endpoint, so edits/deletes here immediately
// reflect in the calendar and vice-versa. Zero data-shape changes.

const STATUS_META = {
  pending:     { label_el: 'Εκκρεμεί',    label_en: 'Pending',     color: '#64748b', bg: 'bg-zinc-100', text: 'text-zinc-700', border: 'border-zinc-200' },
  in_progress: { label_el: 'Σε εξέλιξη',   label_en: 'In Progress', color: '#3b82f6', bg: 'bg-blue-50',  text: 'text-blue-700', border: 'border-blue-200' },
  completed:   { label_el: 'Ολοκληρώθηκε', label_en: 'Completed',   color: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  cancelled:   { label_el: 'Ακυρώθηκε',    label_en: 'Cancelled',   color: '#a1a1aa', bg: 'bg-zinc-50',  text: 'text-zinc-500', border: 'border-zinc-200' },
  overdue:     { label_el: 'Ληξιπρόθεσμο', label_en: 'Overdue',     color: '#ef4444', bg: 'bg-red-50',   text: 'text-red-700', border: 'border-red-200' },
};

const fmtDmy = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return '—';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const StatusPill = ({ status, isEl }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 border ${meta.bg} ${meta.text} ${meta.border}`}>
      {isEl ? meta.label_el : meta.label_en}
    </span>
  );
};

export default function TasksListPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isEl = language === 'el';
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  const [rows, setRows] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fBuilding, setFBuilding] = useState('');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');

  // View / Edit dialog
  const [viewTask, setViewTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Wide window: 3 years back → 3 years forward, then narrow via filters.
      const now = new Date();
      const start = `${now.getFullYear() - 3}-01-01`;
      const end = `${now.getFullYear() + 3}-12-31`;
      const [tRes, bRes, cRes] = await Promise.all([
        axios.get(`${API}/calendar/tasks`, { params: { start, end } }),
        axios.get(`${API}/buildings`).catch(() => ({ data: [] })),
        axios.get(`${API}/task-categories`).catch(() => ({ data: [] })),
      ]);
      setRows(tRes.data?.tasks || []);
      setBuildings(Array.isArray(bRes.data) ? bRes.data : []);
      setCategories(Array.isArray(cRes.data) ? cRes.data : []);
    } catch (e) {
      toast.error(isEl ? 'Αποτυχία φόρτωσης εργασιών' : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [isEl]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Apply filters + search in-memory. Occurrences already carry
  // building_name / category_name so we only need to match strings.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (fCategory && r.category_id !== fCategory) return false;
      if (fStatus && r.status !== fStatus) return false;
      if (fBuilding && r.building_id !== fBuilding) return false;
      if (fDateFrom && (r.occurrence_date || '') < fDateFrom) return false;
      if (fDateTo && (r.occurrence_date || '') > fDateTo) return false;
      if (q) {
        const hay = [r.title, r.notes, r.building_name].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => (a.occurrence_date || '').localeCompare(b.occurrence_date || ''));
  }, [rows, search, fCategory, fStatus, fBuilding, fDateFrom, fDateTo]);

  const anyFilterActive = search || fCategory || fStatus || fBuilding || fDateFrom || fDateTo;
  const clearFilters = () => {
    setSearch(''); setFCategory(''); setFStatus(''); setFBuilding(''); setFDateFrom(''); setFDateTo('');
  };

  // -------------------- View / Edit / Delete --------------------
  const openView = async (row) => {
    try {
      const r = await axios.get(`${API}/tasks/${row.task_id}`);
      setViewTask({ ...r.data, occurrence_date: row.occurrence_date });
    } catch {
      toast.error(isEl ? 'Δεν βρέθηκε' : 'Not found');
    }
  };
  const openEdit = async (row) => {
    if (!canEdit) { toast.error(isEl ? 'Μόνο Admin/Manager' : 'Admin/Manager only'); return; }
    try {
      const r = await axios.get(`${API}/tasks/${row.task_id}`);
      setEditTask({ ...r.data });
    } catch {
      toast.error(isEl ? 'Δεν βρέθηκε' : 'Not found');
    }
  };
  const handleDelete = async (row) => {
    if (!canEdit) { toast.error(isEl ? 'Μόνο Admin/Manager' : 'Admin/Manager only'); return; }
    const q = isEl
      ? (row.is_recurring ? 'Διαγραφή ΟΛΩΝ των εμφανίσεων αυτής της επαναλαμβανόμενης εργασίας;' : 'Διαγραφή εργασίας;')
      : (row.is_recurring ? 'Delete ALL occurrences of this recurring task?' : 'Delete this task?');
    if (!window.confirm(q)) return;
    try {
      await axios.delete(`${API}/calendar/tasks/${row.task_id}/scope`, { params: { scope: 'all' } });
      toast.success(isEl ? 'Διαγράφηκε' : 'Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEl ? 'Αποτυχία' : 'Delete failed'));
    }
  };
  const saveEdit = async () => {
    if (!editTask) return;
    if (!(editTask.title || '').trim()) {
      toast.error(isEl ? 'Απαιτείται τίτλος' : 'Title required');
      return;
    }
    if (!editTask.start_date) {
      toast.error(isEl ? 'Επιλέξτε ημερομηνία' : 'Pick a date');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        building_id: editTask.building_id, type: 'building',
        title: editTask.title || '',
        notes: editTask.notes || '',
        start_date: editTask.start_date,
        end_date: editTask.end_date || null,
        time: editTask.time || null,
        assignee: editTask.assignee || '',
        category_id: editTask.category_id || null,
        is_important: !!editTask.is_important,
        status: editTask.status || 'pending',
        recurrence: editTask.recurrence || null,
        reminders: (editTask.reminders || []).map(r => ({
          offset_minutes: r.offset_minutes, channels: r.channels, reminder_id: r.reminder_id,
        })),
      };
      await axios.put(`${API}/tasks/${editTask.task_id}`, payload);
      toast.success(isEl ? 'Αποθηκεύτηκε' : 'Saved');
      setEditTask(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEl ? 'Αποτυχία αποθήκευσης' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  // -------------------- Render --------------------

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4 sm:py-6" data-testid="tasks-list-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Link
            to="/calendar/tasks"
            className="text-[11px] uppercase tracking-wider px-2 py-1 border border-zinc-300 text-zinc-600 hover:bg-zinc-50 inline-flex items-center gap-1"
            data-testid="back-to-calendar"
          >
            <ArrowLeft size={12} /> {isEl ? 'Ημερολόγιο' : 'Calendar'}
          </Link>
          <div className="flex items-center gap-2 ml-1">
            <ListChecks size={18} className="text-zinc-500" />
            <h1 className="text-lg sm:text-xl font-semibold text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
              {isEl ? 'Διαχείριση Εργασιών' : 'Tasks Management'}
            </h1>
          </div>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200" data-testid="tasks-count-badge">
            {filteredRows.length}
          </span>
          <div className="ml-auto flex items-center gap-2 text-[11px] text-zinc-500">
            {loading ? (isEl ? 'Φόρτωση…' : 'Loading…') : null}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 mb-4" data-testid="tasks-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 p-3">
            <div className="lg:col-span-2 relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isEl ? 'Αναζήτηση σε τίτλο, περιγραφή, πολυκατοικία…' : 'Search title, description, building…'}
                className="pl-7 rounded-none h-9 text-xs"
                data-testid="tasks-search-input"
              />
            </div>
            <select
              value={fCategory}
              onChange={(e) => setFCategory(e.target.value)}
              className="rounded-none border border-zinc-200 px-2 h-9 text-xs bg-white focus:outline-none focus:border-zinc-500"
              data-testid="tasks-category-filter"
            >
              <option value="">{isEl ? 'Όλες οι Κατηγορίες' : 'All Categories'}</option>
              {categories.map(c => (
                <option key={c.category_id} value={c.category_id}>{c.name}</option>
              ))}
            </select>
            <select
              value={fStatus}
              onChange={(e) => setFStatus(e.target.value)}
              className="rounded-none border border-zinc-200 px-2 h-9 text-xs bg-white focus:outline-none focus:border-zinc-500"
              data-testid="tasks-status-filter"
            >
              <option value="">{isEl ? 'Όλες οι Καταστάσεις' : 'All Statuses'}</option>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <option key={k} value={k}>{isEl ? m.label_el : m.label_en}</option>
              ))}
            </select>
            <select
              value={fBuilding}
              onChange={(e) => setFBuilding(e.target.value)}
              className="rounded-none border border-zinc-200 px-2 h-9 text-xs bg-white focus:outline-none focus:border-zinc-500"
              data-testid="tasks-building-filter"
            >
              <option value="">{isEl ? 'Όλες οι Πολυκατοικίες' : 'All Buildings'}</option>
              {buildings.map(b => (
                <option key={b.building_id} value={b.building_id}>{b.name}</option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-2 lg:col-span-1">
              <input
                type="date"
                value={fDateFrom}
                onChange={(e) => setFDateFrom(e.target.value)}
                className="rounded-none border border-zinc-200 px-2 h-9 text-xs bg-white focus:outline-none focus:border-zinc-500 font-mono"
                data-testid="tasks-date-from"
                title={isEl ? 'Από' : 'From'}
              />
              <input
                type="date"
                value={fDateTo}
                onChange={(e) => setFDateTo(e.target.value)}
                className="rounded-none border border-zinc-200 px-2 h-9 text-xs bg-white focus:outline-none focus:border-zinc-500 font-mono"
                data-testid="tasks-date-to"
                title={isEl ? 'Έως' : 'To'}
              />
            </div>
          </div>
          {anyFilterActive && (
            <div className="px-3 pb-2 flex items-center gap-2">
              <FunnelSimple size={12} className="text-zinc-400" />
              <span className="text-[10px] text-zinc-500">
                {isEl ? `${filteredRows.length} αποτελέσματα` : `${filteredRows.length} results`}
              </span>
              <button
                type="button"
                onClick={clearFilters}
                className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 border border-zinc-300 text-zinc-600 hover:bg-zinc-50 inline-flex items-center gap-1"
                data-testid="tasks-clear-filters"
              >
                <X size={10} /> {isEl ? 'Καθαρισμός' : 'Clear'}
              </button>
            </div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block bg-white border border-zinc-200 overflow-x-auto" data-testid="tasks-table">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Τίτλος' : 'Title'}</th>
                <th className="text-left px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Περιγραφή' : 'Description'}</th>
                <th className="text-left px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Πολυκατοικία' : 'Building'}</th>
                <th className="text-left px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Κατηγορία' : 'Category'}</th>
                <th className="text-left px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Ημερομηνία' : 'Date'}</th>
                <th className="text-left px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Ώρα' : 'Time'}</th>
                <th className="text-left px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Κατάσταση' : 'Status'}</th>
                <th className="text-right px-3 py-2 font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">{isEl ? 'Ενέργειες' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-zinc-400 text-sm">
                    {loading ? (isEl ? 'Φόρτωση…' : 'Loading…') : (isEl ? 'Δεν βρέθηκαν εργασίες' : 'No tasks found')}
                  </td>
                </tr>
              ) : filteredRows.map(row => (
                <tr key={row.occurrence_id} className="hover:bg-zinc-50" data-testid={`task-row-${row.occurrence_id}`}>
                  <td className="px-3 py-2 text-zinc-800 font-medium max-w-[220px] truncate" title={row.title} data-testid={`task-title-${row.occurrence_id}`}>
                    {row.is_important && <Warning size={11} weight="fill" className="inline text-amber-500 mr-1" />}
                    {row.title || '—'}
                    {row.is_recurring && <span className="ml-1 text-[9px] uppercase text-zinc-400">↻</span>}
                  </td>
                  <td className="px-3 py-2 text-zinc-600 max-w-[280px] truncate" title={row.notes}>{row.notes || '—'}</td>
                  <td className="px-3 py-2 text-zinc-700 max-w-[180px] truncate" title={row.building_name}>
                    {row.building_id ? (
                      <Link to={`/buildings/${row.building_id}`} className="hover:text-zinc-900 hover:underline">
                        {row.building_name || '—'}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">
                    {row.category_name ? (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border"
                        style={{ background: `${row.category_color || '#a1a1aa'}15`, color: row.category_color || '#3f3f46', borderColor: `${row.category_color || '#e4e4e7'}40` }}
                      >
                        {row.category_name}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-zinc-600 whitespace-nowrap">{fmtDmy(row.occurrence_date)}</td>
                  <td className="px-3 py-2 font-mono text-zinc-500 whitespace-nowrap">{row.time || '—'}</td>
                  <td className="px-3 py-2"><StatusPill status={row.status} isEl={isEl} /></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openView(row)}
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-300 text-zinc-600 hover:bg-zinc-50 mr-1 inline-flex items-center gap-1"
                      data-testid={`task-view-${row.occurrence_id}`}
                    ><Eye size={11} /> {isEl ? 'Προβολή' : 'View'}</button>
                    <button
                      type="button"
                      onClick={() => openEdit(row)}
                      disabled={!canEdit}
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 mr-1 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`task-edit-${row.occurrence_id}`}
                    ><PencilSimple size={11} /> {isEl ? 'Επεξεργασία' : 'Edit'}</button>
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      disabled={!canEdit}
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`task-delete-${row.occurrence_id}`}
                    ><Trash size={11} /> {isEl ? 'Διαγραφή' : 'Delete'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-2" data-testid="tasks-mobile-list">
          {filteredRows.length === 0 && !loading ? (
            <div className="bg-white border border-zinc-200 p-6 text-center text-zinc-400 text-sm">
              {isEl ? 'Δεν βρέθηκαν εργασίες' : 'No tasks found'}
            </div>
          ) : filteredRows.map(row => (
            <div key={row.occurrence_id} className="bg-white border border-zinc-200 p-3" data-testid={`task-card-${row.occurrence_id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-zinc-800 break-words">
                    {row.is_important && <Warning size={12} weight="fill" className="inline text-amber-500 mr-1" />}
                    {row.title || '—'}
                    {row.is_recurring && <span className="ml-1 text-[9px] uppercase text-zinc-400">↻</span>}
                  </p>
                  {row.notes && <p className="text-[11px] text-zinc-600 mt-0.5 break-words">{row.notes}</p>}
                </div>
                <StatusPill status={row.status} isEl={isEl} />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500 mb-2">
                {row.building_name && (
                  <span className="inline-flex items-center gap-1"><CalendarBlank size={10} /> {row.building_name}</span>
                )}
                {row.category_name && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-1 py-0.5 border"
                    style={{ background: `${row.category_color || '#a1a1aa'}15`, color: row.category_color || '#3f3f46', borderColor: `${row.category_color || '#e4e4e7'}40` }}
                  >{row.category_name}</span>
                )}
                <span className="font-mono">{fmtDmy(row.occurrence_date)}{row.time ? ` · ${row.time}` : ''}</span>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => openView(row)} className="flex-1 text-[10px] uppercase tracking-wider px-1.5 py-1 border border-zinc-300 text-zinc-600 hover:bg-zinc-50 inline-flex items-center justify-center gap-1" data-testid={`task-view-m-${row.occurrence_id}`}><Eye size={11} /> {isEl ? 'Προβολή' : 'View'}</button>
                <button type="button" onClick={() => openEdit(row)} disabled={!canEdit} className="flex-1 text-[10px] uppercase tracking-wider px-1.5 py-1 border border-zinc-300 text-zinc-700 hover:bg-zinc-50 inline-flex items-center justify-center gap-1 disabled:opacity-40" data-testid={`task-edit-m-${row.occurrence_id}`}><PencilSimple size={11} /> {isEl ? 'Επεξεργασία' : 'Edit'}</button>
                <button type="button" onClick={() => handleDelete(row)} disabled={!canEdit} className="flex-1 text-[10px] uppercase tracking-wider px-1.5 py-1 border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center justify-center gap-1 disabled:opacity-40" data-testid={`task-delete-m-${row.occurrence_id}`}><Trash size={11} /> {isEl ? 'Διαγραφή' : 'Delete'}</button>
              </div>
            </div>
          ))}
        </div>

        {/* View dialog */}
        <Dialog open={!!viewTask} onOpenChange={(o) => !o && setViewTask(null)}>
          <DialogContent className="max-w-lg" data-testid="task-view-dialog">
            <DialogHeader>
              <DialogTitle>{isEl ? 'Λεπτομέρειες Εργασίας' : 'Task Details'}</DialogTitle>
            </DialogHeader>
            {viewTask && (
              <div className="space-y-2 text-sm">
                <div><span className="text-zinc-500 text-xs">{isEl ? 'Τίτλος' : 'Title'}:</span> <b>{viewTask.title || '—'}</b></div>
                {viewTask.notes && <div><span className="text-zinc-500 text-xs">{isEl ? 'Περιγραφή' : 'Description'}:</span><p className="mt-1 whitespace-pre-wrap text-zinc-700">{viewTask.notes}</p></div>}
                <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
                  <div><span className="text-zinc-500">{isEl ? 'Πολυκατοικία' : 'Building'}:</span> {buildings.find(b => b.building_id === viewTask.building_id)?.name || '—'}</div>
                  <div><span className="text-zinc-500">{isEl ? 'Κατηγορία' : 'Category'}:</span> {categories.find(c => c.category_id === viewTask.category_id)?.name || '—'}</div>
                  <div><span className="text-zinc-500">{isEl ? 'Έναρξη' : 'Start'}:</span> <span className="font-mono">{fmtDmy(viewTask.start_date)}</span></div>
                  <div><span className="text-zinc-500">{isEl ? 'Λήξη' : 'End'}:</span> <span className="font-mono">{fmtDmy(viewTask.end_date) || '—'}</span></div>
                  <div><span className="text-zinc-500">{isEl ? 'Ώρα' : 'Time'}:</span> {viewTask.time || '—'}</div>
                  <div><span className="text-zinc-500">{isEl ? 'Κατάσταση' : 'Status'}:</span> <StatusPill status={viewTask.status} isEl={isEl} /></div>
                  {viewTask.assignee && <div className="col-span-2"><span className="text-zinc-500">{isEl ? 'Ανατέθηκε' : 'Assignee'}:</span> {viewTask.assignee}</div>}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewTask(null)} data-testid="task-view-close">{isEl ? 'Κλείσιμο' : 'Close'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit dialog */}
        <Dialog open={!!editTask} onOpenChange={(o) => !o && setEditTask(null)}>
          <DialogContent className="max-w-lg" data-testid="task-edit-dialog">
            <DialogHeader>
              <DialogTitle>{isEl ? 'Επεξεργασία Εργασίας' : 'Edit Task'}</DialogTitle>
            </DialogHeader>
            {editTask && (
              <div className="space-y-3 text-sm">
                <div>
                  <Label className="text-xs">{isEl ? 'Τίτλος' : 'Title'} *</Label>
                  <Input value={editTask.title || ''} onChange={(e) => setEditTask({ ...editTask, title: e.target.value })} className="rounded-none mt-1" data-testid="edit-title" />
                </div>
                <div>
                  <Label className="text-xs">{isEl ? 'Περιγραφή' : 'Description'}</Label>
                  <Textarea value={editTask.notes || ''} onChange={(e) => setEditTask({ ...editTask, notes: e.target.value })} rows={3} className="rounded-none mt-1" data-testid="edit-notes" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">{isEl ? 'Πολυκατοικία' : 'Building'}</Label>
                    <select value={editTask.building_id || ''} onChange={(e) => setEditTask({ ...editTask, building_id: e.target.value })}
                      className="w-full rounded-none border border-zinc-300 px-2 py-2 text-sm bg-white mt-1" data-testid="edit-building">
                      <option value="">—</option>
                      {buildings.map(b => <option key={b.building_id} value={b.building_id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">{isEl ? 'Κατηγορία' : 'Category'}</Label>
                    <select value={editTask.category_id || ''} onChange={(e) => setEditTask({ ...editTask, category_id: e.target.value })}
                      className="w-full rounded-none border border-zinc-300 px-2 py-2 text-sm bg-white mt-1" data-testid="edit-category">
                      <option value="">—</option>
                      {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">{isEl ? 'Ημερομηνία' : 'Date'} *</Label>
                    <DatePickerDMY value={editTask.start_date} onChange={(v) => setEditTask({ ...editTask, start_date: v })} className="mt-1" data-testid="edit-date" />
                  </div>
                  <div>
                    <Label className="text-xs">{isEl ? 'Λήξη' : 'End'}</Label>
                    <DatePickerDMY value={editTask.end_date} onChange={(v) => setEditTask({ ...editTask, end_date: v })} className="mt-1" data-testid="edit-end" />
                  </div>
                  <div>
                    <Label className="text-xs">{isEl ? 'Ώρα' : 'Time'}</Label>
                    <Input type="time" value={editTask.time || ''} onChange={(e) => setEditTask({ ...editTask, time: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="edit-time" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{isEl ? 'Κατάσταση' : 'Status'}</Label>
                  <select value={editTask.status || 'pending'} onChange={(e) => setEditTask({ ...editTask, status: e.target.value })}
                    className="w-full rounded-none border border-zinc-300 px-2 py-2 text-sm bg-white mt-1" data-testid="edit-status">
                    {Object.entries(STATUS_META).filter(([k]) => k !== 'overdue').map(([k, m]) => (
                      <option key={k} value={k}>{isEl ? m.label_el : m.label_en}</option>
                    ))}
                  </select>
                </div>
                {editTask.recurrence && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2 flex items-start gap-1.5">
                    <Warning size={12} className="mt-0.5 flex-shrink-0" />
                    <span>{isEl ? 'Αυτή είναι επαναλαμβανόμενη εργασία. Οι αλλαγές θα εφαρμοστούν σε όλες τις εμφανίσεις.' : 'This is a recurring task. Changes apply to all occurrences.'}</span>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTask(null)} disabled={saving} data-testid="task-edit-cancel">{isEl ? 'Ακύρωση' : 'Cancel'}</Button>
              <Button onClick={saveEdit} disabled={saving} data-testid="task-edit-save">{saving ? (isEl ? 'Αποθήκευση…' : 'Saving…') : (isEl ? 'Αποθήκευση' : 'Save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
